import { useEffect, useRef } from "react";

/**
 * Poll `fn` every `intervalMs`, but only while the tab is actually being looked
 * at. The dashboards previously ran a bare setInterval, so a backgrounded tab
 * kept requesting for as long as it stayed open — burning battery and mobile
 * data, and multiplying load on the API for no visible benefit.
 *
 * On regaining visibility it refetches immediately, so returning to the tab
 * shows current data rather than up-to-one-interval-old data.
 */
export function usePoll(fn, intervalMs = 5000) {
  const saved = useRef(fn);
  saved.current = fn;

  useEffect(() => {
    let timer = null;
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const start = () => {
      if (timer) return;
      timer = setInterval(() => saved.current(), intervalMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        saved.current(); // catch up on whatever changed while hidden
        start();
      } else {
        stop();
      }
    };

    saved.current();
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [intervalMs]);
}

export default usePoll;
